import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
// Configure PDF.js worker - use local worker file
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
export class FileProcessor {
    static async processFile(file) {
        if (file.size > this.MAX_FILE_SIZE) {
            throw new Error(`File ${file.name} exceeds maximum size of 50MB`);
        }
        const icon = this.getFileIcon(file.type, file.name);
        let content = '';
        try {
            if (file.type === 'application/pdf') {
                content = await this.processPDF(file);
            }
            else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                file.name.endsWith('.docx')) {
                content = await this.processDOCX(file);
            }
            else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel' ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls')) {
                content = await this.processExcel(file);
            }
            else if (file.type.startsWith('text/') || file.name.endsWith('.txt')) {
                content = await this.processText(file);
            }
            else if (file.type.startsWith('image/')) {
                const imageData = await this.processImage(file);
                content = `[Image: ${file.name}]`;
                return {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    content,
                    icon,
                    isImage: true,
                    imageData
                };
            }
            else {
                throw new Error(`Unsupported file type: ${file.type || 'unknown'}`);
            }
            return {
                name: file.name,
                type: file.type,
                size: file.size,
                content,
                icon
            };
        }
        catch (error) {
            throw new Error(`Failed to process ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    static async processPDF(file) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await getDocument({ data: arrayBuffer, disableRange: true, disableStream: true }).promise;
        console.log(`PDF.js version: ${version}, Pages: ${pdf.numPages}`);
        console.log(`PDF Metadata:`, await pdf.getMetadata());
        console.log(`PDF object:`, await pdf);
        const textParts = [];
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items
                .map((item) => item.str)
                .join(' ');
            textParts.push(pageText);
        }
        return textParts.join('\n\n');
    }
    static async processDOCX(file) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        return result.value;
    }
    static async processExcel(file) {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const csvParts = [];
        workbook.SheetNames.forEach(sheetName => {
            const worksheet = workbook.Sheets[sheetName];
            const csv = XLSX.utils.sheet_to_csv(worksheet);
            csvParts.push(`--- Sheet: ${sheetName} ---\n${csv}`);
        });
        return csvParts.join('\n\n');
    }
    static async processText(file) {
        return await file.text();
    }
    static async processImage(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result;
                // Return the full data URL (e.g., "data:image/jpeg;base64,...")
                resolve(result);
            };
            reader.onerror = () => reject(new Error('Failed to read image file'));
            reader.readAsDataURL(file);
        });
    }
    static getFileIcon(type, name) {
        if (type === 'application/pdf' || name.endsWith('.pdf')) {
            return 'file-earmark-pdf';
        }
        else if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            name.endsWith('.docx') ||
            name.endsWith('.doc')) {
            return 'file-earmark-word';
        }
        else if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            type === 'application/vnd.ms-excel' ||
            name.endsWith('.xlsx') ||
            name.endsWith('.xls')) {
            return 'file-earmark-excel';
        }
        else if (type.startsWith('text/') || name.endsWith('.txt')) {
            return 'file-earmark-text';
        }
        else if (type.startsWith('image/')) {
            return 'file-earmark-image';
        }
        return 'file-earmark';
    }
    static formatFileSize(bytes) {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    }
}
FileProcessor.MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
